import { buildStoryBattlePayload } from "./storyBattleRegistry";

// `currentLaunchTarget.kind` is the runtime verb for Story Mode.
// See `storyTargetContract.js` for the full contract: required fields,
// expected interaction loop, and the exact success event that should advance
// the story for each target type.
const STORY_TARGET_KIND = {
  CUTSCENE: "cutscene",
  WORLD: "world",
  NPC: "npc",
  ITEM: "item",
  PUZZLE: "puzzle",
  BATTLE: "battle",
  AWAITING_CONTENT: "awaitingContent",
  COMPLETE: "complete",
};

const STORY_VERSION = 16;

const DEFAULT_WORLD_TARGET = {
  mapId: "voidCity",
  building: "",
  spawnId: "story-start",
  x: null,
  y: null,
};

const DEFAULT_DIALOG_SPEAKER = "Story";
const isLegacyMission = (mission = {}) =>
  String(mission?.missionId || "").includes("-legacy-");

const getActs = (storyData) => storyData?.acts || [];

const getActByNumber = (storyData, actNumber) =>
  getActs(storyData).find((entry) => Number(entry.actNumber) === Number(actNumber)) || null;

const getFirstMissionInAct = (storyData, actNumber) =>
  (getActByNumber(storyData, actNumber)?.missions || []).find(
    (entry) => !isLegacyMission(entry),
  ) || null;

const hasActIntro = (act = {}) => typeof act?.introCutsceneId === "string" && act.introCutsceneId;

const getCutsceneProgress = (progress = {}, cutsceneId) =>
  progress?.cutsceneProgress?.[cutsceneId] || null;

const normalizeDialogLine = (line, mission = {}) => {
  if (!line) return null;

  if (typeof line === "string") {
    const [possibleSpeaker, ...rest] = line.split(":");
    const hasSpeaker = rest.length > 0;
    return {
      speaker: {
        name: hasSpeaker ? possibleSpeaker.trim() : mission.npc || DEFAULT_DIALOG_SPEAKER,
      },
      text: hasSpeaker ? rest.join(":").trim() : line.trim(),
    };
  }

  const extraLineData = Object.entries(line).reduce((acc, [key, value]) => {
    if (["speaker", "text", "choices"].includes(key)) return acc;
    acc[key] = value;
    return acc;
  }, {});

  const normalizedLine = {
    ...extraLineData,
    speaker: {
      name:
        line?.speaker?.name ||
        line?.speaker ||
        mission.npc ||
        DEFAULT_DIALOG_SPEAKER,
      portrait: line?.speaker?.portrait || null,
    },
    text: String(line?.text || "").trim(),
  };

  if (Array.isArray(line?.choices)) {
    normalizedLine.choices = line.choices;
  }

  return normalizedLine;
};

const getMissionInteractionScript = (mission = {}) => {
  const scriptSource = Array.isArray(mission.npcInteraction)
    ? mission.npcInteraction
    : mission.npcDialogue
      ? [mission.npcDialogue]
      : [];

  return scriptSource
    .map((line) => normalizeDialogLine(line, mission))
    .filter((line) => line?.text);
};

export const getMissionById = (storyData, missionId) => {
  for (const act of getActs(storyData)) {
    const mission = (act.missions || []).find((entry) => entry.missionId === missionId);
    if (mission) return mission;
  }
  return null;
};

export const getFirstMission = (storyData) => getActs(storyData)?.[0]?.missions?.[0] || null;

export const getMissionByProgress = (storyData, progress = {}) => {
  if (progress.currentMissionId) {
    const mission = getMissionById(storyData, progress.currentMissionId);
    if (mission) return mission;
  }

  const actNumber = Number(progress.actNumber || progress.currentAct);
  if (!actNumber) return getFirstMission(storyData);

  const act = getActs(storyData).find(
    (entry) => Number(entry.actNumber) === actNumber,
  );
  if (!act) return getFirstMission(storyData);

  const ordinal = Number(progress.missionOrdinalInAct);
  if (ordinal && act.missions?.[ordinal - 1]) return act.missions[ordinal - 1];

  const missionNumber = Number(progress.currentMission);
  if (missionNumber) {
    const mission = act.missions?.find(
      (entry) => Number(entry.missionNumber) === missionNumber,
    );
    if (mission) return mission;
  }

  return act.missions?.[0] || getFirstMission(storyData);
};

export const inferMissionGameplayKind = (mission = {}) => {
  const type = String(mission.type || "").toLowerCase();

  // Keep kind inference focused on "what interaction loop owns this mission
  // beat?" instead of "what flavor text does the mission have?" The target kind
  // should tell runtime code which success event it must wait for.
  if (type.includes("battle")) return STORY_TARGET_KIND.BATTLE;
  if (
    type.includes("pattern") ||
    type.includes("matching") ||
    type.includes("timed") ||
    type.includes("spin wheel") ||
    type.includes("puzzle")
  ) {
    return STORY_TARGET_KIND.PUZZLE;
  }
  if (
    type.includes("item use") ||
    type.includes("item retrieval") ||
    type.includes("item interaction")
  ) {
    return STORY_TARGET_KIND.ITEM;
  }
  if (type.includes("npc interaction")) return STORY_TARGET_KIND.NPC;

  return STORY_TARGET_KIND.WORLD;
};

export const buildMissionSteps = (mission = {}) => {
  if (!mission?.missionId) return [];

  const kind = inferMissionGameplayKind(mission);
  const interactionScript = getMissionInteractionScript(mission);

  // A mission step should describe the active gameplay verb for the current
  // beat. The world may stay mounted throughout, but progression should only
  // move forward when the handler for this kind reports success.
  return [
    {
      id: `${mission.missionId}:gameplay`,
      kind,
      title: mission.missionTitle || "Mission",
      npc: mission.npc || null,
      objective: mission.objective || "",
      location: mission.location || "",
      itemGained: mission.itemGained || null,
      itemUsed: mission.itemUsed || null,
      missionType: mission.type || "",
      text: interactionScript[0]?.text || mission.npcDialogue || "",
      script: interactionScript,
      battleEncounterId: mission.battleEncounterId || null,
      encounterMode: mission.encounterMode || null,
      encounterSetId: mission.encounterSetId || null,
      counterLabel: mission.counterLabel || "",
      triggerZoneId: mission.triggerZoneId || null,
      triggerSceneKey: mission.triggerSceneKey || null,
      interactableId: mission.interactableId || null,
      reveal: mission.reveal || null,
      startMode:
        kind === STORY_TARGET_KIND.NPC
          ? mission.startMode || "interaction"
          : null,
      resolveMode:
        kind === STORY_TARGET_KIND.WORLD
          ? mission.resolveMode || "trigger_only"
          : null,
      hubMode: mission.hubMode || null,
      sideQuestSetId: mission.sideQuestSetId || null,
      requiredStoryLevel: Number(mission.requiredStoryLevel || 0) || null,
      requiredTurnIns: Number(mission.requiredTurnIns || 0) || null,
    },
  ];
};

const isSameWorldScope = (left = {}, right = {}) =>
  String(left?.mapId || DEFAULT_WORLD_TARGET.mapId) ===
    String(right?.mapId || DEFAULT_WORLD_TARGET.mapId) &&
  String(left?.building || "") === String(right?.building || "");

const buildWorldTarget = (
  existingWorld = {},
  baseWorld = {},
  {
    preserveExistingScope = false,
  } = {},
) => {
  const nextWorld = {
    ...DEFAULT_WORLD_TARGET,
    ...baseWorld,
  };

  if (!baseWorld || !Object.keys(baseWorld).length) {
    return {
      ...nextWorld,
      ...existingWorld,
    };
  }

  if (preserveExistingScope) {
    return {
      ...nextWorld,
      ...existingWorld,
    };
  }

  if (isSameWorldScope(existingWorld, baseWorld)) {
    return {
      ...nextWorld,
      ...existingWorld,
    };
  }

  return nextWorld;
};

const buildCutsceneLaunchTarget = (act, progress = {}, existingTarget = {}) => {
  const cutsceneId = act?.introCutsceneId;
  const cutsceneState = getCutsceneProgress(progress, cutsceneId) || {};

  return {
    kind: STORY_TARGET_KIND.CUTSCENE,
    cutsceneId,
    actNumber: act?.actNumber ?? 1,
    actTitle: act?.actTitle || "",
    missionId: progress.currentMissionId || getFirstMissionInAct({ acts: [act] }, act?.actNumber)?.missionId,
    missionNumber: progress.currentMission ?? 1,
    stepId: `${cutsceneId}:cutscene`,
    stepIndex: 0,
    title: `${act?.actTitle || "Story"} Intro`,
    objective: "Watch the story introduction.",
    world: buildWorldTarget(existingTarget.world, act?.introWorld),
    cutsceneState: {
      completed: Boolean(cutsceneState.completed),
      currentPage: Number(cutsceneState.currentPage ?? 0),
      completedPanels: Array.isArray(cutsceneState.completedPanels)
        ? cutsceneState.completedPanels
        : [],
    },
    updatedAt: Date.now(),
  };
};

const buildAwaitingContentLaunchTarget = (mission, existingTarget = {}) => ({
  kind: STORY_TARGET_KIND.AWAITING_CONTENT,
  missionId: mission?.missionId,
  actNumber: mission?.actNumber,
  missionNumber: mission?.missionNumber,
  stepId: `${mission?.missionId}:awaiting-content`,
  stepIndex: 0,
  title: mission?.awaitingContentTitle || "Combat Training Unlocked",
  subtitle: mission?.subtitle || "",
  objective:
    mission?.awaitingContentObjective ||
    "Arena training is unlocked. More story missions will continue from here in a later pass.",
  npc: mission?.npc || null,
  missionType: mission?.type || "",
  world: buildWorldTarget(existingTarget.world, mission?.world, {
    preserveExistingScope: existingTarget?.missionId === mission?.missionId,
  }),
  updatedAt: Date.now(),
});

export const buildLaunchTarget = (mission, step, existingTarget = {}) => {
  if (!mission || !step) {
    return {
      kind: STORY_TARGET_KIND.COMPLETE,
      title: "Story Complete",
      objective: "You have completed the current story arc.",
    };
  }

  const world = buildWorldTarget(existingTarget.world, mission.world, {
    preserveExistingScope: existingTarget?.missionId === mission?.missionId,
  });
  const baseLaunchTarget = {
    // This launch target is the handoff object between story progression and
    // runtime orchestration. Keep kind-specific fields on it so scenes and HUD
    // layers can decide which interaction loop is currently responsible for
    // advancing Story Mode.
    kind: step.kind,
    missionId: mission.missionId,
    actNumber: mission.actNumber,
    missionNumber: mission.missionNumber,
    stepId: step.id,
    stepIndex: existingTarget.stepIndex ?? 0,
    title: step.title || mission.missionTitle || "Mission",
    subtitle: mission.subtitle || "",
    objective: step.objective || mission.objective || "",
    npc: step.npc || mission.npc || null,
    missionType: mission.type || "",
    itemGained: mission.itemGained || null,
    itemUsed: mission.itemUsed || null,
    interactableId: step.interactableId || mission.interactableId || null,
    encounterMode: step.encounterMode || mission.encounterMode || null,
    encounterSetId: step.encounterSetId || mission.encounterSetId || null,
    counterLabel: step.counterLabel || mission.counterLabel || "",
    reveal: step.reveal || mission.reveal || null,
    text: step.text || mission.npcDialogue || "",
    script: Array.isArray(step.script) ? step.script : [],
    triggerZoneId: step.triggerZoneId || mission.triggerZoneId || null,
    triggerSceneKey: step.triggerSceneKey || mission.triggerSceneKey || null,
    startMode:
      step.kind === STORY_TARGET_KIND.NPC
        ? step.startMode || mission.startMode || "interaction"
        : null,
    resolveMode:
      step.kind === STORY_TARGET_KIND.WORLD
        ? step.resolveMode || mission.resolveMode || "trigger_only"
        : null,
    hubMode: step.hubMode || mission.hubMode || null,
    sideQuestSetId: step.sideQuestSetId || mission.sideQuestSetId || null,
    requiredStoryLevel:
      step.requiredStoryLevel || mission.requiredStoryLevel || null,
    requiredTurnIns: step.requiredTurnIns || mission.requiredTurnIns || null,
    world,
    updatedAt: Date.now(),
  };

  if (step.kind === STORY_TARGET_KIND.BATTLE) {
    const battlePayload = buildStoryBattlePayload({
      mission,
      world,
      existingBattleState: existingTarget?.battleState || {},
      existingBattleEncounterId: existingTarget?.battle?.encounterId || null,
    });

    return {
      ...baseLaunchTarget,
      ...battlePayload,
    };
  }

  return baseLaunchTarget;
};

export const getCurrentStoryBeat = (storyData, progress = {}) => {
  const mission = getMissionByProgress(storyData, progress);
  if (!mission) return { mission: null, steps: [], step: null, stepIndex: 0 };

  const steps = buildMissionSteps(mission);
  const requestedIndex = Number(progress.currentStepIndex ?? 0);
  const stepIndex = Math.min(Math.max(requestedIndex, 0), Math.max(steps.length - 1, 0));
  const step = steps[stepIndex] || null;

  return { mission, steps, step, stepIndex };
};

const buildProgressShape = (mission, stepIndex, currentLaunchTarget, patch = {}) => ({
  arc: patch.arc || mission?.arc || "Neon Embers",
  status: patch.status || "in_progress",
  currentAct: mission?.actNumber ?? patch.currentAct ?? 1,
  actNumber: mission?.actNumber ?? patch.actNumber ?? 1,
  actTitle: mission?.actTitle || patch.actTitle || "",
  currentMission: mission?.missionNumber ?? patch.currentMission ?? 1,
  currentMissionId: mission?.missionId || patch.currentMissionId || null,
  missionOrdinalInAct: patch.missionOrdinalInAct ?? 1,
  currentStepIndex: stepIndex,
  currentLaunchTarget,
  lastCompletedMissionId: patch.lastCompletedMissionId || null,
  cutsceneProgress: patch.cutsceneProgress || {},
  updatedAt: patch.updatedAt || Date.now(),
});

const getMissionOrdinalInAct = (storyData, mission) => {
  const act = getActs(storyData).find(
    (entry) => Number(entry.actNumber) === Number(mission?.actNumber),
  );
  if (!act || !mission) return 1;

  const index = (act.missions || [])
    .filter((entry) => !isLegacyMission(entry))
    .findIndex(
    (entry) => entry.missionId === mission.missionId,
  );
  return index >= 0 ? index + 1 : 1;
};

const shouldLaunchActIntro = (storyData, progress = {}, mission = null) => {
  if (!mission) return false;

  const act = getActByNumber(storyData, mission.actNumber);
  const firstMission = getFirstMissionInAct(storyData, mission.actNumber);
  if (!hasActIntro(act) || !firstMission || firstMission.missionId !== mission.missionId) {
    return false;
  }

  const cutsceneState = getCutsceneProgress(progress, act.introCutsceneId);
  return !cutsceneState?.completed;
};

const migrateLegacyProgress = (storyData, progress = {}) => {
  if (Number(progress.storyVersion || 0) >= STORY_VERSION) {
    return { ...progress, storyVersion: STORY_VERSION };
  }

  const legacyStoryVersion = Number(progress.storyVersion || 0);
  const mission = getMissionByProgress(storyData, progress);
  const act = getActByNumber(storyData, mission?.actNumber);
  const existingTarget = progress.currentLaunchTarget || {};
  const pendingActIntro =
    mission &&
    act &&
    shouldLaunchActIntro(storyData, progress, mission);
  const shouldAdvancePastTrainingIntro =
    legacyStoryVersion < STORY_VERSION &&
    progress?.status === "awaiting_content" &&
    progress?.currentMissionId === "act-1-mission-5";
  const shouldRedirectToCompanionUnlock =
    legacyStoryVersion < STORY_VERSION &&
    progress?.status === "awaiting_content" &&
    progress?.currentMissionId === "act-1-mission-8";
  const shouldAdvancePastCompanionEndpoint =
    legacyStoryVersion < STORY_VERSION &&
    progress?.status === "awaiting_content" &&
    progress?.currentMissionId === "act-1-mission-9";
  const shouldAdvancePastGlydeWaitingEndpoint =
    legacyStoryVersion < STORY_VERSION &&
    progress?.status === "awaiting_content" &&
    progress?.currentMissionId === "act-1-mission-11";
  const shouldAdvancePastRuneStoneReturnEndpoint =
    legacyStoryVersion < STORY_VERSION &&
    progress?.status === "awaiting_content" &&
    progress?.currentMissionId === "act-1-mission-13";
  const shouldReplayGlydeReturnMission =
    legacyStoryVersion < 10 &&
    progress?.status === "awaiting_content" &&
    progress?.currentMissionId === "act-1-mission-14";
  const shouldAdvanceIntoActTwoIntro =
    legacyStoryVersion >= 10 &&
    legacyStoryVersion < STORY_VERSION &&
    progress?.status === "awaiting_content" &&
    progress?.currentMissionId === "act-1-mission-14";
  const shouldAdvancePastActTwoMissionOneEndpoint =
    legacyStoryVersion >= 10 &&
    legacyStoryVersion < STORY_VERSION &&
    progress?.status === "awaiting_content" &&
    progress?.currentMissionId === "act-2-mission-1";
  const shouldAdvancePastActTwoMissionTwoEndpoint =
    legacyStoryVersion < STORY_VERSION &&
    progress?.status === "awaiting_content" &&
    progress?.currentMissionId === "act-2-mission-2";
  const shouldAdvancePastActTwoMissionThreeEndpoint =
    legacyStoryVersion < STORY_VERSION &&
    progress?.status === "awaiting_content" &&
    progress?.currentMissionId === "act-2-mission-3";

  return {
    ...progress,
    storyVersion: STORY_VERSION,
    status:
      shouldAdvancePastTrainingIntro ||
      shouldRedirectToCompanionUnlock ||
      shouldAdvancePastCompanionEndpoint ||
      shouldAdvancePastGlydeWaitingEndpoint ||
      shouldAdvancePastRuneStoneReturnEndpoint ||
      shouldReplayGlydeReturnMission ||
      shouldAdvanceIntoActTwoIntro ||
      shouldAdvancePastActTwoMissionOneEndpoint ||
      shouldAdvancePastActTwoMissionTwoEndpoint ||
      shouldAdvancePastActTwoMissionThreeEndpoint
        ? "in_progress"
        : progress.status,
    currentMissionId: shouldAdvancePastTrainingIntro
      ? "act-1-mission-6"
    : shouldAdvancePastCompanionEndpoint
        ? "act-1-mission-10"
      : shouldAdvancePastRuneStoneReturnEndpoint
        ? "act-1-mission-15"
      : shouldAdvanceIntoActTwoIntro
        ? "act-2-mission-1"
      : shouldAdvancePastActTwoMissionOneEndpoint
        ? "act-2-mission-2"
      : shouldAdvancePastActTwoMissionTwoEndpoint
        ? "act-2-mission-3"
      : shouldAdvancePastActTwoMissionThreeEndpoint
        ? "act-2-mission-4"
      : shouldReplayGlydeReturnMission
        ? "act-1-mission-15"
      : shouldAdvancePastGlydeWaitingEndpoint
        ? "act-1-mission-12"
      : shouldRedirectToCompanionUnlock
        ? "act-1-mission-9"
      : progress.currentMissionId,
    currentMission: shouldAdvancePastTrainingIntro
      ? 6
      : shouldAdvancePastCompanionEndpoint
        ? 10
      : shouldAdvancePastRuneStoneReturnEndpoint
        ? 15
      : shouldAdvanceIntoActTwoIntro
        ? 1
      : shouldAdvancePastActTwoMissionOneEndpoint
        ? 2
      : shouldAdvancePastActTwoMissionTwoEndpoint
        ? 3
      : shouldAdvancePastActTwoMissionThreeEndpoint
        ? 4
      : shouldReplayGlydeReturnMission
        ? 15
      : shouldAdvancePastGlydeWaitingEndpoint
        ? 12
      : shouldRedirectToCompanionUnlock
        ? 9
        : progress.currentMission,
    currentAct:
      shouldAdvancePastTrainingIntro ||
      shouldRedirectToCompanionUnlock ||
      shouldAdvancePastCompanionEndpoint ||
      shouldAdvancePastGlydeWaitingEndpoint ||
      shouldAdvancePastRuneStoneReturnEndpoint ||
      shouldReplayGlydeReturnMission
      ? 1
      : shouldAdvanceIntoActTwoIntro ||
          shouldAdvancePastActTwoMissionOneEndpoint ||
          shouldAdvancePastActTwoMissionTwoEndpoint ||
          shouldAdvancePastActTwoMissionThreeEndpoint
        ? 2
      : progress.currentAct,
    actNumber:
      shouldAdvancePastTrainingIntro ||
      shouldRedirectToCompanionUnlock ||
      shouldAdvancePastCompanionEndpoint ||
      shouldAdvancePastGlydeWaitingEndpoint ||
      shouldAdvancePastRuneStoneReturnEndpoint ||
      shouldReplayGlydeReturnMission
        ? 1
      : shouldAdvanceIntoActTwoIntro ||
          shouldAdvancePastActTwoMissionOneEndpoint ||
          shouldAdvancePastActTwoMissionTwoEndpoint ||
          shouldAdvancePastActTwoMissionThreeEndpoint
        ? 2
        : progress.actNumber,
    actTitle:
      shouldAdvancePastTrainingIntro ||
      shouldRedirectToCompanionUnlock ||
      shouldAdvancePastCompanionEndpoint ||
      shouldAdvancePastGlydeWaitingEndpoint ||
      shouldAdvancePastRuneStoneReturnEndpoint ||
      shouldReplayGlydeReturnMission
      ? "The Rift Tide"
      : shouldAdvanceIntoActTwoIntro ||
          shouldAdvancePastActTwoMissionOneEndpoint ||
          shouldAdvancePastActTwoMissionTwoEndpoint ||
          shouldAdvancePastActTwoMissionThreeEndpoint
        ? "Whispers in the Wind"
      : progress.actTitle,
    currentStepIndex: 0,
    lastCompletedMissionId: shouldAdvancePastTrainingIntro
      ? "act-1-mission-5"
      : shouldAdvancePastCompanionEndpoint
        ? "act-1-mission-9"
      : shouldAdvancePastRuneStoneReturnEndpoint
        ? "act-1-mission-14"
      : shouldAdvanceIntoActTwoIntro
        ? "act-1-mission-15"
      : shouldAdvancePastActTwoMissionOneEndpoint
        ? "act-2-mission-1"
      : shouldAdvancePastActTwoMissionTwoEndpoint
        ? "act-2-mission-2"
      : shouldAdvancePastActTwoMissionThreeEndpoint
        ? "act-2-mission-3"
      : shouldReplayGlydeReturnMission
        ? "act-1-mission-14"
      : shouldAdvancePastGlydeWaitingEndpoint
        ? "act-1-mission-11"
      : shouldRedirectToCompanionUnlock
        ? "act-1-mission-8"
      : progress.lastCompletedMissionId,
    currentLaunchTarget:
      pendingActIntro && hasActIntro(act)
        ? buildCutsceneLaunchTarget(act, progress, existingTarget)
        : shouldAdvancePastTrainingIntro ||
            shouldRedirectToCompanionUnlock ||
            shouldAdvancePastCompanionEndpoint ||
            shouldAdvancePastGlydeWaitingEndpoint ||
            shouldAdvancePastRuneStoneReturnEndpoint ||
            shouldReplayGlydeReturnMission ||
            shouldAdvanceIntoActTwoIntro ||
            shouldAdvancePastActTwoMissionOneEndpoint ||
            shouldAdvancePastActTwoMissionTwoEndpoint ||
            shouldAdvancePastActTwoMissionThreeEndpoint
          ? null
          : progress.currentLaunchTarget || null,
  };
};

export const resolveStoryProgress = (storyData, rawProgress = {}) => {
  const progress = migrateLegacyProgress(storyData, rawProgress);
  const mission = getMissionByProgress(storyData, progress) || getFirstMission(storyData);

  if (!mission) {
    return {
      progress: {
        arc: storyData?.arc || "Story",
        status: "completed",
        storyVersion: STORY_VERSION,
        currentLaunchTarget: {
          kind: STORY_TARGET_KIND.COMPLETE,
          title: "Story Complete",
          objective: "No story missions are configured yet.",
        },
      },
      didMigrate: Number(rawProgress.storyVersion || 0) < STORY_VERSION,
    };
  }

  const act = getActByNumber(storyData, mission.actNumber);
  const shouldUseActIntro = shouldLaunchActIntro(storyData, progress, mission);

  if (shouldUseActIntro && hasActIntro(act)) {
    const currentLaunchTarget = buildCutsceneLaunchTarget(
      act,
      progress,
      progress.currentLaunchTarget || {},
    );

    return {
      progress: {
        storyVersion: STORY_VERSION,
        startedAt: progress.startedAt || Date.now(),
        ...buildProgressShape(mission, 0, currentLaunchTarget, {
          ...progress,
          arc: storyData?.arc || progress.arc || "Story",
          missionOrdinalInAct: getMissionOrdinalInAct(storyData, mission),
        }),
      },
      didMigrate: Number(rawProgress.storyVersion || 0) < STORY_VERSION,
    };
  }

  if (
    progress.status === "awaiting_content" &&
    mission?.completionState === "awaiting_content"
  ) {
    const currentLaunchTarget = buildAwaitingContentLaunchTarget(
      mission,
      progress.currentLaunchTarget || {},
    );

    return {
      progress: {
        storyVersion: STORY_VERSION,
        startedAt: progress.startedAt || Date.now(),
        ...buildProgressShape(mission, 0, currentLaunchTarget, {
          ...progress,
          arc: storyData?.arc || progress.arc || "Story",
          status: "awaiting_content",
          missionOrdinalInAct: getMissionOrdinalInAct(storyData, mission),
        }),
      },
      didMigrate: Number(rawProgress.storyVersion || 0) < STORY_VERSION,
    };
  }

  const { step, stepIndex } = getCurrentStoryBeat(storyData, {
    ...progress,
    currentMissionId: mission.missionId,
    currentStepIndex: progress.currentStepIndex ?? 0,
  });
  const currentLaunchTarget = buildLaunchTarget(mission, step, progress.currentLaunchTarget || {});

  return {
    progress: {
      storyVersion: STORY_VERSION,
      startedAt: progress.startedAt || Date.now(),
      ...buildProgressShape(mission, stepIndex, currentLaunchTarget, {
        ...progress,
        arc: storyData?.arc || progress.arc || "Story",
        missionOrdinalInAct: getMissionOrdinalInAct(storyData, mission),
      }),
    },
    didMigrate: Number(rawProgress.storyVersion || 0) < STORY_VERSION,
  };
};

export const buildInitialStoryProgress = (storyData, patch = {}) =>
  resolveStoryProgress(storyData, patch).progress;

const getNextMission = (storyData, mission) => {
  if (!mission) return null;
  const act = getActs(storyData).find(
    (entry) => Number(entry.actNumber) === Number(mission.actNumber),
  );
  const missions = (act?.missions || []).filter((entry) => !isLegacyMission(entry));
  const missionIndex = missions.findIndex((entry) => entry.missionId === mission.missionId);
  if (missionIndex >= 0 && missionIndex < missions.length - 1) {
    return missions[missionIndex + 1];
  }

  const actIndex = getActs(storyData).findIndex(
    (entry) => Number(entry.actNumber) === Number(mission.actNumber),
  );
  const nextAct = getActs(storyData)[actIndex + 1];
  return (nextAct?.missions || []).find((entry) => !isLegacyMission(entry)) || null;
};

export const advanceStoryProgress = (storyData, progress = {}) => {
  const resolvedProgress = buildInitialStoryProgress(storyData, progress);

  if (resolvedProgress.currentLaunchTarget?.kind === STORY_TARGET_KIND.CUTSCENE) {
    const act = getActByNumber(storyData, resolvedProgress.currentAct);
    const cutsceneId = act?.introCutsceneId;
    const cutsceneProgress = {
      ...(resolvedProgress.cutsceneProgress || {}),
      [cutsceneId]: {
        ...getCutsceneProgress(resolvedProgress, cutsceneId),
        completed: true,
        currentPage: 0,
        completedPanels: [],
        completedAt: Date.now(),
      },
    };

    return buildInitialStoryProgress(storyData, {
      ...resolvedProgress,
      cutsceneProgress,
      currentLaunchTarget: {
        ...resolvedProgress.currentLaunchTarget,
        world: resolvedProgress.currentLaunchTarget?.world,
      },
    });
  }

  const { mission, steps, stepIndex } = getCurrentStoryBeat(storyData, resolvedProgress);
  if (!mission) return buildInitialStoryProgress(storyData, resolvedProgress);

  const nextStep = steps[stepIndex + 1];
  if (nextStep) {
    return buildInitialStoryProgress(storyData, {
      ...resolvedProgress,
      currentStepIndex: stepIndex + 1,
      currentLaunchTarget: buildLaunchTarget(mission, nextStep, {
        ...resolvedProgress.currentLaunchTarget,
        stepIndex: stepIndex + 1,
      }),
    });
  }

  if (mission?.completionState === "awaiting_content") {
    return {
      ...resolvedProgress,
      storyVersion: STORY_VERSION,
      status: "awaiting_content",
      completedAt: null,
      lastCompletedMissionId: mission.missionId,
      currentLaunchTarget: buildAwaitingContentLaunchTarget(
        mission,
        resolvedProgress.currentLaunchTarget || {},
      ),
      updatedAt: Date.now(),
    };
  }

  const nextMission = getNextMission(storyData, mission);
  if (!nextMission) {
    return {
      ...resolvedProgress,
      storyVersion: STORY_VERSION,
      status: "completed",
      completedAt: Date.now(),
      lastCompletedMissionId: mission.missionId,
      currentLaunchTarget: {
        kind: STORY_TARGET_KIND.COMPLETE,
        missionId: mission.missionId,
        title: "Story Complete",
        objective: "You have completed the current story arc.",
      },
      updatedAt: Date.now(),
    };
  }

  return buildInitialStoryProgress(storyData, {
    ...resolvedProgress,
    currentMissionId: nextMission.missionId,
    currentMission: nextMission.missionNumber,
    currentAct: nextMission.actNumber,
    actNumber: nextMission.actNumber,
    actTitle: nextMission.actTitle || "",
    currentStepIndex: 0,
    // Mission boundaries must not carry kind-specific runtime state forward.
    // In particular, Story battle rooms/results belong to the drill that
    // created them; the next battle target needs a fresh `not_started` state.
    currentLaunchTarget: {
      stepIndex: 0,
      world: resolvedProgress.currentLaunchTarget?.world,
    },
    lastCompletedMissionId: mission.missionId,
  });
};

export const withStoryLaunchTarget = (storyData, progress = {}, targetPatch = {}) => {
  const resolvedProgress = buildInitialStoryProgress(storyData, progress);

  if (
    resolvedProgress.currentLaunchTarget?.kind === STORY_TARGET_KIND.CUTSCENE &&
    resolvedProgress.currentLaunchTarget?.cutsceneId
  ) {
    return {
      ...resolvedProgress,
      storyVersion: STORY_VERSION,
      currentLaunchTarget: {
        ...resolvedProgress.currentLaunchTarget,
        ...targetPatch,
        world: targetPatch.world
          ? {
              ...resolvedProgress.currentLaunchTarget.world,
              ...targetPatch.world,
            }
          : resolvedProgress.currentLaunchTarget.world,
      },
      updatedAt: Date.now(),
    };
  }

  if (
    resolvedProgress.currentLaunchTarget?.kind === STORY_TARGET_KIND.AWAITING_CONTENT
  ) {
    return {
      ...resolvedProgress,
      storyVersion: STORY_VERSION,
      currentLaunchTarget: {
        ...resolvedProgress.currentLaunchTarget,
        ...targetPatch,
        world: targetPatch.world
          ? {
              ...resolvedProgress.currentLaunchTarget.world,
              ...targetPatch.world,
            }
          : resolvedProgress.currentLaunchTarget.world,
      },
      updatedAt: Date.now(),
    };
  }

  const { mission, step, stepIndex } = getCurrentStoryBeat(storyData, resolvedProgress);
  const currentLaunchTarget = buildLaunchTarget(mission, step, {
    ...resolvedProgress.currentLaunchTarget,
    ...targetPatch,
    battleState: targetPatch.battleState
      ? {
          ...(resolvedProgress.currentLaunchTarget?.battleState || {}),
          ...targetPatch.battleState,
        }
      : resolvedProgress.currentLaunchTarget?.battleState,
    world: targetPatch.world
      ? {
          ...resolvedProgress.currentLaunchTarget?.world,
          ...targetPatch.world,
        }
      : resolvedProgress.currentLaunchTarget?.world,
    stepIndex,
  });

  return {
    ...resolvedProgress,
    storyVersion: STORY_VERSION,
    currentLaunchTarget,
    updatedAt: Date.now(),
  };
};

export const withStoryCutsceneProgress = (
  storyData,
  progress = {},
  cutsceneId,
  patch = {},
) => {
  const resolvedProgress = buildInitialStoryProgress(storyData, progress);
  const nextCutsceneProgress = {
    ...(resolvedProgress.cutsceneProgress || {}),
    [cutsceneId]: {
      ...getCutsceneProgress(resolvedProgress, cutsceneId),
      ...patch,
    },
  };

  return buildInitialStoryProgress(storyData, {
    ...resolvedProgress,
    cutsceneProgress: nextCutsceneProgress,
  });
};

export const STORY_TARGET_KINDS = STORY_TARGET_KIND;
